import type {
  DeprecatedTabsContentOption,
  DeprecatedTabsTriggerOption,
} from "@v1/components/designSystem/tabs/Tabs";
import type { FormKey } from "@v1/contexts/ElementEditorErrorContext";
import type { ElementErrorType } from "@v1/utils/element";
import type { ReploElementType } from "schemas/generated/element";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import { useElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";
import { useElementEditorErrorContext } from "@v1/contexts/ElementEditorErrorContext";
import { useShopifyBlogs } from "@v1/hooks/element";
import { useInitial } from "@v1/hooks/useInitial";
import useOnChangeMetafields from "@v1/hooks/useOnChangeMetafields";
import { selectIsShopifyIntegrationEnabled } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import validatePageModalPath from "@v1/utils/validatePageModalPath";
import { slugify } from "replo-utils/lib/string";

import ImageSourceSelector from "../page/element-editor/components/modifiers/ImageSourceSelector";
import ElementCustomCssEditor from "./ElementCustomCssEditor";
import { ElementEditorErrors } from "./ElementEditorErrors";
import ElementNameEditor from "./ElementNameEditor";
import SeoElementEditor from "./SeoElementEditor";
import { ShopifyThemeSettings } from "./ShopifyThemeSettings";

type ArticleElementEditorTab = "general" | "shopifyTheme" | "advanced";

const ARTICLE_ELEMENT_EDITOR_TABS: DeprecatedTabsTriggerOption[] = [
  { value: "general", label: "General" },
  { value: "shopifyTheme", label: "Shopify Theme" },
  { value: "advanced", label: "Advanced" },
];

const ArticleElementEditor: React.FC = () => {
  const [selectedTab, setSelectedTab] =
    React.useState<ArticleElementEditorTab>("general");

  const ARTICLE_ELEMENT_EDITOR_CONTENT: DeprecatedTabsContentOption[] = [
    { value: "general", tabContent: <ArticleBasicSettings /> },
    { value: "shopifyTheme", tabContent: <ShopifyThemeSettings /> },
    {
      value: "advanced",
      tabContent: <ElementCustomCssEditor />,
    },
  ];
  return (
    <div className="flex h-fit flex-col">
      <DeprecatedTabs
        tabsOptions={ARTICLE_ELEMENT_EDITOR_TABS}
        contentOptions={ARTICLE_ELEMENT_EDITOR_CONTENT}
        value={selectedTab}
        onValueChange={(value) => {
          setSelectedTab(value as ArticleElementEditorTab);
        }}
        size="sm"
        defaultValue={selectedTab}
      />
    </div>
  );
};

const ArticleBasicSettings: React.FC = () => {
  const { element, onChangeElement } = useElementEditorDataContext();
  const { errorMapping, setErrors, clearErrors } =
    useElementEditorErrorContext();

  const initialElementIsPublished = useInitial(element.isPublished);
  const { metafields, onMetafieldChange } = useOnChangeMetafields(
    element,
    onChangeElement,
  );

  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );

  const { shopifyBlogs } = useShopifyBlogs("shopifyArticle");

  const handleNameChange = (name: string) => {
    const shopifyPagePath = !element?.shopifyPagePath
      ? slugify(name)
      : element.shopifyPagePath;
    if (errorMapping["path"]?.includes("pathAlreadyExists")) {
      clearErrors("path", ["pathAlreadyExists"]);
    }
    onChangeElement({
      ...element,
      name,
      shopifyPagePath,
    });
  };

  const handlePathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const shopifyPagePath = e.target.value;

    if (shopifyPagePath.length === 0) {
      setErrors("path", ["pathIsEmpty"]);
    } else {
      clearErrors("path", ["pathIsEmpty"]);
      if (validatePageModalPath(shopifyPagePath)) {
        setErrors("path", ["isInvalidPathName"]);
      } else {
        clearErrors("path", ["isInvalidPathName"]);
      }
    }

    if (errorMapping["path"]?.includes("pathAlreadyExists")) {
      clearErrors("path", ["pathAlreadyExists"]);
    }
    if (errorMapping["path"]?.includes("pathIsEmpty")) {
      clearErrors("path", ["pathIsEmpty"]);
    }

    onChangeElement({
      ...element,
      shopifyPagePath,
    });
  };

  const handleRouteChange = (value: string) => {
    if (isShopifyIntegrationEnabled) {
      const shopifyBlogId = shopifyBlogs?.find(
        (blog) => blog.handle === value.split("/")[1],
      )?.id;

      onChangeElement({
        ...element,
        type: "shopifyArticle",
        shopifyBlogId,
      });
    }
  };

  const routes: { value: string; isSelected: boolean }[] = [];

  if (isShopifyIntegrationEnabled) {
    shopifyBlogs?.forEach((blog) => {
      return routes.push({
        value: `blogs/${blog.handle}/`,
        isSelected: blog.id === element.shopifyBlogId,
      });
    });
  }

  return (
    <div className="flex flex-col gap-y-4">
      <ElementNameEditor
        type="page"
        inputName="page-name"
        initialName={element.name}
        onChange={handleNameChange}
      />

      <DeprecatedLabeledControl
        label="Post Featured Image"
        id="page-data-featured-image"
        size="sm"
      >
        <ImageSourceSelector
          src={element.shopifyArticleImage?.src}
          onChangeImageSource={(value: string) => {
            onChangeElement({
              ...element,
              shopifyArticleImage: { src: value },
            });
          }}
          onRemove={() => {
            return onChangeElement({
              ...element,
              shopifyArticleImage: { src: "" },
            });
          }}
          allowsDynamicData={false}
          size="sm"
        />
      </DeprecatedLabeledControl>

      <DeprecatedLabeledControl
        label="URL"
        id="page-data-path"
        size="sm"
        error={
          <PathInputError
            errorMapping={errorMapping}
            elementType={element.type}
          />
        }
      >
        <div className="flex flex-row items-center gap-2">
          <div className="flex-none">
            <Combobox.Root
              options={routes.map((route) => ({
                label: route.value,
                value: route.value,
              }))}
              value={routes.find((route) => route.isSelected)?.value}
              onChange={(value) => handleRouteChange(value)}
            >
              <Combobox.Trigger>
                <Combobox.SelectionButton
                  size="sm"
                  layoutClassName="w-24"
                  title={routes.find((route) => route.isSelected)?.value ?? ""}
                />
              </Combobox.Trigger>
              <Combobox.Popover layoutClassName="w-48">
                <Combobox.Content />
              </Combobox.Popover>
            </Combobox.Root>
          </div>
          <DeprecatedInput
            id="page-data-path"
            value={element.shopifyPagePath}
            placeholder="path"
            size="sm"
            onChange={handlePathChange}
          />
        </div>
      </DeprecatedLabeledControl>

      {initialElementIsPublished && (
        <SwitchWithDescription
          label="Is Published"
          description="Public users can see this page."
          isOn={element.isPublished}
          onChange={(isPublished) => {
            return onChangeElement({ ...element, isPublished });
          }}
          size="sm"
          variant="card"
        />
      )}

      <Separator />

      <SeoElementEditor
        isPageSettings={element.type === "page"}
        metafields={metafields}
        onMetafieldChange={onMetafieldChange}
      />
    </div>
  );
};

const PathInputError: React.FC<{
  errorMapping: Record<FormKey, ElementErrorType[] | null>;
  elementType: ReploElementType;
}> = ({ errorMapping, elementType }) => {
  const isError =
    errorMapping &&
    Object.keys(errorMapping).some((key) => {
      return errorMapping[key as FormKey]?.some((error) => {
        return [
          "pathAlreadyExists",
          "isInvalidPathName",
          "pathIsEmpty",
          "loadingMissingData",
          "errorLoadingData",
          "storeHasNoBlogs",
        ].includes(error);
      });
    });

  if (isError) {
    const errors = Object.values(errorMapping).flat();
    const primaryErrorType = errors.find((error) => {
      return error?.toLowerCase().includes("path") && typeof error === "string";
    });
    const errorType = primaryErrorType ?? errors[0];
    if (errorType) {
      return (
        <ElementEditorErrors errorType={errorType} elementType={elementType} />
      );
    }
  }

  return null;
};

export default ArticleElementEditor;
